import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Trash2, KeyRound, Users, Ticket, Plus, Copy, UsersRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const ROLE_LABEL = { admin: "Admin", sub_admin: "Sub-admin", user: "User" };

export default function AdminTab() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const isAdmin = user?.role === "admin";

  const load = () => api.adminUsers().then(setUsers).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const resetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.username}:`);
    if (!pw) return;
    try { await api.adminResetPassword(u.id, pw); toast.success(`Password reset for ${u.username}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const changeRole = async (u, role) => {
    try { await api.adminSetRole(u.id, role); toast.success(`${u.username} is now ${ROLE_LABEL[role]}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.username} and all their data? This cannot be undone.`)) return;
    try { await api.adminDeleteUser(u.id); toast.success(`Deleted ${u.username}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <Card className="bg-[#121824]/70 border-slate-800 p-4 flex items-center gap-3">
        <ShieldCheck className="h-5 w-5 text-amber-400" />
        <div>
          <h3 className="font-display font-bold uppercase tracking-wide text-sm">User Management</h3>
          <p className="text-xs text-slate-500">Reset passwords, {isAdmin ? "change roles, " : ""}and delete accounts. {isAdmin ? "Full admins can manage everyone." : "Sub-admins can manage regular users only."}</p>
        </div>
      </Card>

      {isAdmin && <BetaAccess />}

      <TeamsAdmin />

      <Card className="bg-[#121824]/70 border-slate-800 p-4 sm:p-5" data-testid="admin-users-card">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-cyan-400" />
          <h3 className="font-display font-bold uppercase tracking-wide text-sm">Accounts <span className="text-slate-500 font-mono">({users.length})</span></h3>
        </div>
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 flex items-center gap-3 flex-wrap" data-testid="admin-user-row">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-100 truncate">{u.username}
                  {u.id === user?.id && <span className="ml-2 text-[10px] font-mono bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded">YOU</span>}
                </p>
                <p className="text-[11px] text-slate-500 font-mono">{ROLE_LABEL[u.role] || u.role}</p>
              </div>
              {isAdmin && u.id !== user?.id && (
                <Select value={u.role} onValueChange={(r) => changeRole(u, r)}>
                  <SelectTrigger data-testid="admin-role-select" className="w-32 h-8 bg-slate-950/60 border-slate-700 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="sub_admin">Sub-admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Button data-testid="admin-reset-pw-button" onClick={() => resetPw(u)} size="sm" variant="outline"
                className="border-slate-700 bg-transparent text-cyan-300 hover:bg-cyan-950/30 h-8">
                <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset
              </Button>
              {u.id !== user?.id && (
                <Button data-testid="admin-delete-user-button" onClick={() => del(u)} variant="ghost" size="icon" className="text-slate-500 hover:text-red-400 h-8 w-8">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function BetaAccess() {
  const [enabled, setEnabled] = useState(false);
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [note, setNote] = useState("");
  const [loaded, setLoaded] = useState(false);

  const load = () =>
    api.adminGetBeta().then((r) => { setEnabled(!!r.enabled); setCodes(r.codes || []); setLoaded(true); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const toggle = async (v) => {
    setEnabled(v);
    try { await api.adminToggleBeta(v); toast.success(v ? "Beta gate ON — new sign-ups need a code" : "Beta gate OFF — registration is open"); }
    catch (e) { setEnabled(!v); toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const create = async () => {
    try {
      const c = await api.adminCreateBetaCode(newCode.trim() || null, note.trim() || null);
      toast.success(`Code ${c.code} created`);
      setNewCode(""); setNote(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setActive = async (c, active) => {
    try { await api.adminUpdateBetaCode(c.id, active); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete code ${c.code}? People who have it will no longer be able to register.`)) return;
    try { await api.adminDeleteBetaCode(c.id); toast.success(`Deleted ${c.code}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success(`Copied ${code}`); };

  return (
    <Card className="bg-[#121824]/70 border-slate-800 p-4 sm:p-5" data-testid="admin-beta-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Ticket className="h-4 w-4 text-amber-400" />
          <h3 className="font-display font-bold uppercase tracking-wide text-sm">Beta Access</h3>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <span>{enabled ? "Gate ON · code required" : "Gate OFF · open sign-up"}</span>
          <Switch data-testid="admin-beta-toggle" checked={enabled} onCheckedChange={toggle} disabled={!loaded} />
        </label>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        When the gate is ON, new accounts must enter one of the active codes below. Existing users and admins are unaffected.
      </p>

      <div className="flex items-end gap-2 flex-wrap mb-4">
        <div className="flex-1 min-w-[140px]">
          <p className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">New code (optional)</p>
          <Input data-testid="admin-beta-new-code" value={newCode} onChange={(e) => setNewCode(e.target.value)}
            placeholder="leave blank to auto-generate" className="h-9 bg-slate-950/60 border-slate-700 text-sm font-mono uppercase tracking-wider" />
        </div>
        <div className="flex-1 min-w-[140px]">
          <p className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">Note (optional)</p>
          <Input data-testid="admin-beta-new-note" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Twitter wave 1" className="h-9 bg-slate-950/60 border-slate-700 text-sm" />
        </div>
        <Button data-testid="admin-beta-create" onClick={create} className="h-9 bg-amber-500 text-slate-900 hover:bg-amber-400 font-bold">
          <Plus className="h-4 w-4 mr-1" /> Add code
        </Button>
      </div>

      <div className="space-y-2">
        {codes.length === 0 && <p className="text-xs text-slate-500">No codes yet — add one to let beta testers sign up.</p>}
        {codes.map((c) => (
          <div key={c.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 flex items-center gap-3 flex-wrap" data-testid="admin-beta-row">
            <button onClick={() => copy(c.code)} data-testid="admin-beta-copy" className="font-mono text-base font-bold text-amber-300 tracking-widest flex items-center gap-1.5 hover:text-amber-200">
              {c.code} <Copy className="h-3.5 w-3.5 opacity-60" />
            </button>
            <div className="flex-1 min-w-0">
              {c.note && <p className="text-xs text-slate-400 truncate">{c.note}</p>}
              <p className="text-[11px] text-slate-500 font-mono">{c.uses || 0} sign-up{(c.uses || 0) === 1 ? "" : "s"}{c.active ? "" : " · inactive"}</p>
            </div>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-400">
              {c.active ? "Active" : "Off"}
              <Switch data-testid="admin-beta-row-toggle" checked={!!c.active} onCheckedChange={(v) => setActive(c, v)} />
            </label>
            <Button data-testid="admin-beta-delete" onClick={() => del(c)} variant="ghost" size="icon" className="text-slate-500 hover:text-red-400 h-8 w-8">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}



function TeamsAdmin() {
  const [teams, setTeams] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = () =>
    api.teamDirectory().then((t) => { setTeams(t || []); setLoaded(true); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const del = async (t) => {
    if (!window.confirm(`Delete team "${t.name}"? This permanently removes the team, its pairing setup and its saved history. This cannot be undone.`)) return;
    try { await api.deleteTeam(t.id); toast.success(`Deleted "${t.name}"`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="bg-[#121824]/70 border-slate-800 p-4 sm:p-5" data-testid="admin-teams-card">
      <div className="flex items-center gap-2 mb-1">
        <UsersRound className="h-4 w-4 text-amber-400" />
        <h3 className="font-display font-bold uppercase tracking-wide text-sm">Team Management</h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">Delete any team workspace. This also removes its pairing session and saved history.</p>

      <div className="space-y-2">
        {loaded && teams.length === 0 && <p className="text-xs text-slate-500">No teams yet.</p>}
        {teams.map((t) => (
          <div key={t.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 flex items-center gap-3 flex-wrap" data-testid="admin-team-row">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-200 truncate">{t.name}</p>
              <p className="text-[11px] text-slate-500 font-mono truncate">
                {t.event_name ? `${t.event_name} · ` : ""}captain {t.captain} · {t.member_count} member{t.member_count === 1 ? "" : "s"}
              </p>
            </div>
            <Button data-testid="admin-team-delete" onClick={() => del(t)} variant="ghost" size="sm" className="text-slate-500 hover:text-red-400 hover:bg-red-500/10">
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
